import useAssistantChat, { AssistantAIMode, AssistantChatStatus } from 'ai-assistant/hooks/useAssistantChat';
import {
  getRegisteredAssistantMentionLabels,
  registerAssistantMentionLabel,
} from 'ai-assistant/utils/assistantChatCommands';
import {
  getMentionChipLabel,
  parseInputIntoParts,
  parseMentionToken,
} from 'ai-assistant/utils/mentionTokens';
import { Button, Icon, Intent } from '@blueprintjs/core';
import { Popover2InteractionKind, Tooltip2 } from '@blueprintjs/popover2';
import { selectCurrentModel, setCurrentModel } from 'controllers/common/action';
import {
  selectReadExistingModel,
  selectStoredModelInputs,
  selectUnsavedModelInputs,
} from 'controllers/models/action';
import { get as getValueOnPath, isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { constructPath, findRoute, routes } from 'routes';

import './AssistantChatContent.light.scss';
import { AssistantMessage } from '../assistant-message/AssistantMessage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, LayoutGroup } from 'framer-motion';

interface AssistantChatContentProps {
  chat: ReturnType<typeof useAssistantChat>;
  isSmallScreen: boolean;
  onComposerHeightChange?: (height: number) => void;
}

const logAnalyticsEvent = (_event: { category: string; action: string; label?: string; value?: number }) => {
  // TODO
};

const MENTION_AUTOCOMPLETE_REGEX = /(^|\s)@([^\s@]*)$/;
const MODEL_INPUT_CONTAINER_SELECTOR = '[data-assistant-context="model-input-container"]';
const FORM_GROUP_SELECTOR = '.bp3-form-group, .bp4-form-group';
const FORM_CONTROL_SELECTOR = 'input, select, textarea, [contenteditable="true"], [role="textbox"], [role="spinbutton"]';
const EDITABLE_ROLES = new Set(['textbox', 'spinbutton']);
const MAX_MENTION_OPTIONS = 12;

const navigateToModelRoute = () => {
  const modelPath = constructPath(findRoute(routes, 'Model'));
  if (!modelPath) {
    return;
  }

  window.location.hash = modelPath;
};

const isEditableControl = (control: HTMLElement): boolean => {
  if (control instanceof HTMLInputElement) {
    if (control.type === 'hidden' || control.disabled) {
      return false;
    }
    if (control.readOnly && control.type !== 'radio' && control.type !== 'checkbox' && control.type !== 'range') {
      return false;
    }
    return true;
  }

  if (control instanceof HTMLTextAreaElement) {
    return !(control.disabled || control.readOnly);
  }

  if (control instanceof HTMLSelectElement) {
    return !control.disabled;
  }

  if (control.hasAttribute('contenteditable')) {
    return control.getAttribute('contenteditable') !== 'false';
  }

  const role = control.getAttribute('role');
  if (role && EDITABLE_ROLES.has(role)) {
    const ariaDisabled = control.getAttribute('aria-disabled');
    const ariaReadOnly = control.getAttribute('aria-readonly');
    return ariaDisabled !== 'true' && ariaReadOnly !== 'true';
  }

  return false;
};

interface MentionOption {
  path: string;
  label: string;
}

const getMentionLabelFromControl = (control: HTMLElement, fallbackPath: string): string => {
  const group = control.closest(FORM_GROUP_SELECTOR);
  const labelElement = group?.querySelector('label');
  const ariaLabel = control.getAttribute('aria-label')?.trim();
  const labelledBy = control.getAttribute('aria-labelledby');
  const labelledByText = labelledBy ? document.getElementById(labelledBy)?.textContent?.trim() : undefined;
  const placeholder = control.getAttribute('placeholder')?.trim();

  return labelElement?.textContent?.trim() || ariaLabel || labelledByText || placeholder || fallbackPath;
};

const formatMentionValue = (value: unknown): string => {
  if (value === undefined) {
    return '(undefined)';
  }

  if (value === null) {
    return 'null';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return String(value);
  }
};

const appendNodeText = (node: Node, chunks: string[]) => {
  if (node.nodeType === Node.TEXT_NODE) {
    chunks.push(node.textContent ?? '');
    return;
  }

  if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
    Array.from(node.childNodes).forEach(child => appendNodeText(child, chunks));
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const element = node as HTMLElement;
  if (element.dataset.mentionTag) {
    chunks.push(element.dataset.mentionTag);
    return;
  }

  if (element.tagName === 'BR') {
    chunks.push('\n');
    return;
  }

  Array.from(element.childNodes).forEach(child => appendNodeText(child, chunks));

  if (element.tagName === 'DIV' || element.tagName === 'P') {
    chunks.push('\n');
  }
};

const readNodeText = (node: Node): string => {
  const chunks: string[] = [];
  appendNodeText(node, chunks);
  return chunks.join('');
};

const readEditableText = (root: HTMLElement): string => {
  const value = readNodeText(root);
  return value.replace(/\u00A0/g, ' ').replace(/\n$/, '');
};

const getTextBeforeCaret = (root: HTMLElement): string | null => {
  const selection = window.getSelection();
  if (!selection || !selection.isCollapsed || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer)) {
    return null;
  }

  const prefixRange = range.cloneRange();
  prefixRange.selectNodeContents(root);
  prefixRange.setEnd(range.startContainer, range.startOffset);
  const value = readNodeText(prefixRange.cloneContents());
  return value.replace(/\u00A0/g, ' ');
};

const placeCaretAtEnd = (element: HTMLElement) => {
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);

  selection.removeAllRanges();
  selection.addRange(range);
};

const placeCaretAtTextOffset = (root: HTMLElement, targetOffset: number) => {
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const range = document.createRange();
  let remaining = Math.max(0, targetOffset);
  let positioned = false;

  const placeAfterNode = (node: Node) => {
    range.setStartAfter(node);
    range.collapse(true);
    positioned = true;
  };

  const walk = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (remaining <= text.length) {
        range.setStart(node, remaining);
        range.collapse(true);
        positioned = true;
        return true;
      }
      remaining -= text.length;
      return false;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (element.dataset.mentionTag) {
        const mentionLength = element.dataset.mentionTag.length;
        if (remaining <= mentionLength) {
          placeAfterNode(element);
          return true;
        }
        remaining -= mentionLength;
        return false;
      }

      if (element.tagName === 'BR') {
        if (remaining <= 1) {
          placeAfterNode(element);
          return true;
        }
        remaining -= 1;
        return false;
      }
    }

    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (walk(child)) {
        return true;
      }
    }

    return false;
  };

  walk(root);

  if (!positioned) {
    range.selectNodeContents(root);
    range.collapse(false);
  }

  selection.removeAllRanges();
  selection.addRange(range);
};

export function AssistantChatContent({ chat, isSmallScreen, onComposerHeightChange }: AssistantChatContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const pendingCaretOffsetRef = useRef<number | null>(null);
  const dispatch = useDispatch();
  const currentModel = useSelector(selectCurrentModel);
  const readExistingModel = useSelector(selectReadExistingModel);
  const storedModelInputs = useSelector(selectStoredModelInputs);
  const unsavedModelInputs = useSelector(selectUnsavedModelInputs);
  const [mentionAutocomplete, setMentionAutocomplete] = useState<{
    query: string;
    start: number;
    end: number;
  } | null>(null);
  const [mentionOptions, setMentionOptions] = useState<MentionOption[]>([]);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const shouldShowChangeSummary = chat.isModelPage && chat.hasModelChanges;
  const pendingApprovalCount = chat.pendingToolApprovalCount ?? 0;
  const hasPendingApprovals = pendingApprovalCount > 0;
  const pendingApprovalLabel = `${pendingApprovalCount} proposed ${pendingApprovalCount === 1 ? 'change' : 'changes'} awaiting approval`;
  const totalChanges = chat.modelChangeCount ?? 0;
  const changeSummaryLabel = `${totalChanges} ${totalChanges === 1 ? 'CHANGE' : 'CHANGES'} TOTAL`;
  const { aiMode, isModelPage, isOpen, isViewOnlyModel, setAIMode } = chat;
  const isInputEmpty = chat.input.trim().length === 0;
  const associatedModel = chat.sessionModelAssociation;
  const associatedModelName = associatedModel?.modelName?.trim();
  const associatedModelKey = associatedModel?.modelKey?.trim() || (associatedModelName ? `saved:${associatedModelName}` : null);
  const shouldShowOpenAssociatedModel = Boolean(
    associatedModelName
    && (!chat.isModelPage || chat.currentModelAssociation?.modelKey !== associatedModelKey)
  );

  const handleOpenAssociatedModel = useCallback(() => {
    if (!associatedModelName) {
      return;
    }

    dispatch(setCurrentModel({
      modelName: associatedModelName,
      modelType: associatedModel?.modelType ?? undefined,
    }));
    navigateToModelRoute();
  }, [associatedModel, associatedModelName, dispatch]);

  const mentionTooltipByTag = useMemo<Record<string, string>>(() => {
    const parts = parseInputIntoParts(chat.input);
    const mentionTags = Array.from(
      new Set(
        parts
          .filter(part => part.type === 'mention')
          .map(part => part.value),
      ),
    );

    const currentModelName = currentModel?.modelName;
    const currentDraftId = currentModel?.draftId;

    const getCurrentInputForModelKey = (modelKey: string): unknown => {
      if (currentModelName && modelKey === currentModelName) {
        return readExistingModel?.input ?? currentModel?.input;
      }

      if (currentDraftId && modelKey === currentDraftId) {
        return unsavedModelInputs?.[modelKey] ?? currentModel?.input;
      }

      return unsavedModelInputs?.[modelKey] ?? storedModelInputs?.[modelKey];
    };

    const tooltipByTag: Record<string, string> = {};
    mentionTags.forEach(tag => {
      const parsed = parseMentionToken(tag);
      if (!parsed) {
        return;
      }

      const { modelKey, path } = parsed;
      const aiInput = getCurrentInputForModelKey(modelKey);
      const originalInput =
        storedModelInputs && Object.prototype.hasOwnProperty.call(storedModelInputs, modelKey)
          ? storedModelInputs[modelKey]
          : aiInput;

      const originalValue = getValueOnPath(originalInput as any, path);
      const aiValue = getValueOnPath(aiInput as any, path);
      const isAiDifferent = !isEqual(originalValue, aiValue);

      const lines = [
        `Original model value:`,
        formatMentionValue(originalValue),
      ];

      if (isAiDifferent) {
        lines.push('', 'AI model value:', formatMentionValue(aiValue));
      }

      tooltipByTag[tag] = lines.join('\n');
    });

    return tooltipByTag;
  }, [chat.input, currentModel, readExistingModel, storedModelInputs, unsavedModelInputs]);

  const closeMentionAutocomplete = useCallback(() => {
    setMentionAutocomplete(null);
    setMentionOptions([]);
    setMentionActiveIndex(0);
  }, []);

  const collectMentionOptions = useCallback((): MentionOption[] => {
    const container = document.querySelector<HTMLElement>(MODEL_INPUT_CONTAINER_SELECTOR);
    if (!container) {
      return [];
    }

    const controls = Array.from(container.querySelectorAll<HTMLElement>(FORM_CONTROL_SELECTOR));
    const optionByPath = new Map<string, MentionOption>();

    controls.forEach(control => {
      if (!isEditableControl(control)) {
        return;
      }

      const path = (control.getAttribute('name') || control.id || '').trim();
      if (!path || optionByPath.has(path)) {
        return;
      }

      const label = getMentionLabelFromControl(control, path);
      optionByPath.set(path, { path, label });
    });

    return Array.from(optionByPath.values());
  }, []);

  const buildMentionOptions = useCallback((query: string): MentionOption[] => {
    const allOptions = collectMentionOptions();

    const normalizedQuery = query.trim().toLowerCase();
    const filtered = normalizedQuery
      ? allOptions.filter(option => (
        option.label.toLowerCase().includes(normalizedQuery) ||
        option.path.toLowerCase().includes(normalizedQuery)
      ))
      : allOptions;

    return filtered
      .map(option => {
        const labelLower = option.label.toLowerCase();
        const pathLower = option.path.toLowerCase();
        const startsWith = normalizedQuery.length > 0 && (
          labelLower.startsWith(normalizedQuery) ||
          pathLower.startsWith(normalizedQuery)
        );
        const score = startsWith ? 2 : (normalizedQuery.length > 0 ? 1 : 0);
        return { option, score };
      })
      .sort((a, b) => b.score - a.score || a.option.label.localeCompare(b.option.label))
      .slice(0, MAX_MENTION_OPTIONS)
      .map(item => item.option);
  }, [collectMentionOptions]);

  const updateMentionAutocomplete = useCallback(() => {
    const inputElement = inputRef.current;
    const modelKey = currentModel?.modelName || currentModel?.draftId;
    if (!inputElement || !modelKey) {
      closeMentionAutocomplete();
      return;
    }

    const textBeforeCaret = getTextBeforeCaret(inputElement);
    if (textBeforeCaret === null) {
      closeMentionAutocomplete();
      return;
    }

    const match = textBeforeCaret.match(MENTION_AUTOCOMPLETE_REGEX);
    if (!match) {
      closeMentionAutocomplete();
      return;
    }

    const query = match[2] ?? '';
    const triggerStart = textBeforeCaret.length - (query.length + 1);
    const nextOptions = buildMentionOptions(query);
    const shouldResetIndex = mentionAutocomplete?.query !== query;

    setMentionAutocomplete(prev => {
      if (prev && prev.query === query && prev.start === triggerStart && prev.end === textBeforeCaret.length) {
        return prev;
      }
      return {
        query,
        start: triggerStart,
        end: textBeforeCaret.length,
      };
    });
    setMentionOptions(nextOptions);
    setMentionActiveIndex(prevIndex => {
      if (nextOptions.length === 0) {
        return 0;
      }
      if (shouldResetIndex) {
        return 0;
      }
      return Math.min(prevIndex, nextOptions.length - 1);
    });
  }, [buildMentionOptions, closeMentionAutocomplete, currentModel, mentionAutocomplete]);

  const insertMentionFromOption = useCallback((option: MentionOption) => {
    const inputElement = inputRef.current;
    const modelKey = currentModel?.modelName || currentModel?.draftId;
    if (!inputElement || !modelKey || !mentionAutocomplete) {
      return;
    }

    const fullText = readEditableText(inputElement);
    const mentionToken = `@${modelKey}:input:${option.path}`;
    registerAssistantMentionLabel(option.path, option.label);
    const suffix = fullText.slice(mentionAutocomplete.end).replace(/^\s+/, '');
    const inserted = `${mentionToken} `;
    const nextText = `${fullText.slice(0, mentionAutocomplete.start)}${inserted}${suffix}`;

    pendingCaretOffsetRef.current = mentionAutocomplete.start + inserted.length;
    chat.setInputValue(nextText);
    closeMentionAutocomplete();
  }, [chat, closeMentionAutocomplete, currentModel, mentionAutocomplete]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (contentRef.current) {
      const scrollContainer = contentRef.current;
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior
      });
    }
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      chat.status === AssistantChatStatus.SUBMITTED
      || chat.status === AssistantChatStatus.STREAMING
      || hasPendingApprovals
    ) {
      return;
    }

    if (chat.input.trim() === '') return;

    scrollToBottom();

    chat.coordinator.withStagedUserMessage(chat.input, async newMessageId => {
      chat.handleSubmit(newMessageId);
    });

    logAnalyticsEvent({
      category: 'AI Assistant',
      action: 'sent_ai_chat_message',
      label: `Message length: ${chat.input}`,
      value: chat.input.length
    });
  };

  useEffect(() => {
    const handleToggleShortcut = (event: KeyboardEvent) => {
      if (!isModelPage || !isOpen || isViewOnlyModel) return;
      if (!(event.key === 'Tab' && event.shiftKey)) return;

      const target = event.target as HTMLElement | null;
      if (composerRef.current && target && !composerRef.current.contains(target)) {
        return;
      }

      event.preventDefault();
      setAIMode(aiMode === AssistantAIMode.ASK ? AssistantAIMode.EDIT : AssistantAIMode.ASK);
    };

    window.addEventListener('keydown', handleToggleShortcut);
    return () => window.removeEventListener('keydown', handleToggleShortcut);
  }, [aiMode, isModelPage, isOpen, isViewOnlyModel, setAIMode]);

  useEffect(() => {
    if (chat.status !== AssistantChatStatus.STREAMING) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      scrollToBottom('auto');
    });

    return () => cancelAnimationFrame(frame);
  }, [
    chat.status,
    chat.messages,
    chat.assistantMessageBlocksById,
    chat.toolExecutionsByMessage,
    scrollToBottom
  ]);

  useEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement) {
      return;
    }

    const domValue = readEditableText(inputElement);
    if (domValue === chat.input) {
      return;
    }

    inputElement.innerHTML = '';
    const fragment = document.createDocumentFragment();
    const parts = parseInputIntoParts(chat.input);
    // Prefer explicitly registered labels (e.g. from field ASK/EDIT buttons), then DOM-derived ones.
    const mentionLabelByPath = {
      ...collectMentionOptions().reduce<Record<string, string>>((labels, option) => {
        labels[option.path] = option.label;
        return labels;
      }, {}),
      ...getRegisteredAssistantMentionLabels(),
    };

    parts.forEach(part => {
      if (part.type === 'mention') {
        const chip = document.createElement('span');
        chip.className = 'assistant-chat-container__mention-chip';
        chip.contentEditable = 'false';
        chip.dataset.mentionTag = part.value;

        const icon = document.createElement('span');
        icon.className = 'assistant-chat-container__mention-chip-icon';
        icon.textContent = '@';

        const label = document.createElement('span');
        label.className = 'assistant-chat-container__mention-chip-label';
        label.textContent = getMentionChipLabel(part.value, mentionLabelByPath);

        const chipTooltip = mentionTooltipByTag[part.value];
        if (chipTooltip) {
          chip.title = chipTooltip;
        }

        chip.appendChild(icon);
        chip.appendChild(label);
        fragment.appendChild(chip);
      } else {
        fragment.appendChild(document.createTextNode(part.value));
      }
    });

    inputElement.appendChild(fragment);
    if (document.activeElement === inputElement) {
      if (pendingCaretOffsetRef.current !== null) {
        placeCaretAtTextOffset(inputElement, pendingCaretOffsetRef.current);
        pendingCaretOffsetRef.current = null;
      } else {
        placeCaretAtEnd(inputElement);
      }
    }
  }, [chat.input, collectMentionOptions, mentionTooltipByTag]);

  useEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement) {
      return;
    }

    const chips = inputElement.querySelectorAll<HTMLElement>('.assistant-chat-container__mention-chip');
    chips.forEach(chip => {
      const tag = chip.dataset.mentionTag;
      if (!tag) {
        return;
      }

      const chipTooltip = mentionTooltipByTag[tag];
      if (chipTooltip) {
        chip.title = chipTooltip;
      } else {
        chip.removeAttribute('title');
      }
    });
  }, [mentionTooltipByTag]);

  useEffect(() => {
    if (!chat.input) {
      closeMentionAutocomplete();
    }
  }, [chat.input, closeMentionAutocomplete]);

  useEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement || !onComposerHeightChange || typeof ResizeObserver === 'undefined') {
      return;
    }

    const notifyComposerHeight = () => {
      onComposerHeightChange(inputElement.clientHeight);
    };

    notifyComposerHeight();
    const observer = new ResizeObserver(notifyComposerHeight);
    observer.observe(inputElement);
    return () => observer.disconnect();
  }, [onComposerHeightChange]);

  const handleEditableInput = useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    chat.setInputValue(readEditableText(inputRef.current));
    updateMentionAutocomplete();
  }, [chat, updateMentionAutocomplete]);

  const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (mentionAutocomplete) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (mentionOptions.length > 0) {
          setMentionActiveIndex(current => (current + 1) % mentionOptions.length);
        }
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (mentionOptions.length > 0) {
          setMentionActiveIndex(current => (current - 1 + mentionOptions.length) % mentionOptions.length);
        }
        return;
      }

      if ((event.key === 'Enter' || event.key === 'Tab') && mentionOptions.length > 0) {
        event.preventDefault();
        const selectedOption = mentionOptions[mentionActiveIndex] ?? mentionOptions[0];
        if (selectedOption) {
          insertMentionFromOption(selectedOption);
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMentionAutocomplete();
        return;
      }
    }

    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      // The composer is a contentEditable <div>, so it doesn't have a `.form` property.
      // Submit the closest wrapping <form> instead.
      const form = event.currentTarget.closest('form');
      if (form instanceof HTMLFormElement) {
        form.requestSubmit();
      }
      return;
    }

    if (event.key !== 'Backspace') {
      return;
    }

    const selection = window.getSelection();
    if (!selection || !selection.isCollapsed || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    const { startContainer, startOffset } = range;
    let chipToRemove: HTMLElement | null = null;

    if (startContainer.nodeType === Node.TEXT_NODE) {
      if (startOffset !== 0) {
        return;
      }

      const previousNode = startContainer.previousSibling;
      if (previousNode instanceof HTMLElement && previousNode.dataset.mentionTag) {
        chipToRemove = previousNode;
      }
    } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
      const element = startContainer as HTMLElement;
      const previousNode = element.childNodes[startOffset - 1] as Node | undefined;
      if (previousNode instanceof HTMLElement && previousNode.dataset.mentionTag) {
        chipToRemove = previousNode;
      }
    }

    if (!chipToRemove) {
      return;
    }

    event.preventDefault();
    chipToRemove.remove();
    handleEditableInput();
  };

  const handleComposerKeyUp = () => {
    updateMentionAutocomplete();
  };

  return (
    <LayoutGroup>
      <div className='assistant-chat-container'>
        <div className='assistant-chat-container__content' ref={contentRef}>
          {chat.messages.map((message, index) => (
            <AssistantMessage
              key={message.id}
              message={message}
              chat={chat}
              isMostRecentMessage={index === chat.messages.length - 1}
            />
          ))}
        </div>

        <div className='assistant-chat-composer' ref={composerRef}>
          {shouldShowOpenAssociatedModel && associatedModel?.modelName && (
            <div className='assistant-chat-associated-model'>
              <div className='assistant-chat-associated-model__label'>
                <Icon icon='cube' iconSize={13} />
                <span>{associatedModel.modelName}</span>
              </div>
              <Button
                small
                minimal
                icon='share'
                text='Open model'
                className='assistant-chat-associated-model__button'
                onClick={handleOpenAssociatedModel}
              />
            </div>
          )}

          {shouldShowChangeSummary && (
            <div className='assistant-chat-change-summary'>
              <div className='assistant-chat-change-summary__header'>
                <span className='assistant-chat-change-summary__count'>{changeSummaryLabel}</span>
                <div className='assistant-chat-change-summary__actions'>
                  <button
                    type="button"
                    className='assistant-chat-change-summary__button assistant-chat-change-summary__button--keep'
                    onClick={chat.handleKeepAllChanges}
                  >
                    Keep All
                  </button>
                  <button
                    type="button"
                    className='assistant-chat-change-summary__button assistant-chat-change-summary__button--undo'
                    onClick={chat.handleUndoAllChanges}
                  >
                    Undo All
                  </button>
                </div>
              </div>
              <div className='assistant-chat-change-summary__divider' aria-hidden />
            </div>
          )}

          {hasPendingApprovals && (
            <div className='assistant-chat-review-summary' role="status" aria-live="polite">
              <div className='assistant-chat-review-summary__header'>
                <span className='assistant-chat-review-summary__count'>{pendingApprovalLabel}</span>
                <div className='assistant-chat-review-summary__actions'>
                  <button
                    type="button"
                    className='assistant-chat-review-summary__button assistant-chat-review-summary__button--apply'
                    onClick={chat.approvePendingToolChanges}
                  >
                    Review Changes
                  </button>
                  <button
                    type="button"
                    className='assistant-chat-review-summary__button assistant-chat-review-summary__button--reject'
                    onClick={chat.rejectPendingToolChanges}
                  >
                    Reject
                  </button>
                </div>
              </div>
              <div className='assistant-chat-review-summary__divider' aria-hidden />
            </div>
          )}

          {chat.isModelPage && (
            <div className='assistant-chat-mode-row'>
              <div className='assistant-chat-mode-row__label'>
                <span>AI Mode</span>
                <Tooltip2
                  popoverClassName='tooltip-content text-tooltip assistant-chat-mode-row__tooltip'
                  placement='right'
                  interactionKind={Popover2InteractionKind.HOVER_TARGET_ONLY}
                  hoverOpenDelay={50}
                  hoverCloseDelay={200}
                  usePortal
                  content={
                    <div className='assistant-chat-mode-row__tooltip-content'>
                      <p><strong>Ask</strong> Mode: for questions, explanations, or quick guidance.</p>
                      {!chat.isViewOnlyModel && (
                        <p><strong>Edit</strong> Mode: to get suggestions oriented around updating the current model or draft.</p>
                      )}
                    </div>
                  }
                >
                  <Icon
                    icon='help'
                    intent={Intent.PRIMARY}
                    aria-label='How AI modes work'
                    title='How AI modes work'
                    className='assistant-chat-mode-row__help-button'
                  />
                </Tooltip2>
              </div>
              <div
                className={`assistant-chat-mode-toggle ${chat.isViewOnlyModel ? 'assistant-chat-mode-toggle--ask-only' : chat.aiMode === AssistantAIMode.ASK ? 'assistant-chat-mode-toggle--ask-active' : 'assistant-chat-mode-toggle--edit-active'}`}
              >
                <button
                  type="button"
                  className={`assistant-chat-mode-toggle__option${chat.aiMode === AssistantAIMode.ASK ? ' is-active' : ''}`}
                  onClick={() => chat.setAIMode(AssistantAIMode.ASK)}
                  aria-pressed={chat.aiMode === AssistantAIMode.ASK}
                >
                  Ask
                </button>
                {!chat.isViewOnlyModel && (
                  <button
                    type="button"
                    className={`assistant-chat-mode-toggle__option${chat.aiMode === AssistantAIMode.EDIT ? ' is-active' : ''}`}
                    onClick={() => chat.setAIMode(AssistantAIMode.EDIT)}
                    aria-pressed={chat.aiMode === AssistantAIMode.EDIT}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          )}

          {isSmallScreen && (
            <div className='assistant-chat-mobile-notice' role="note">
              <Icon
                icon='info-sign'
                size={12}
                className='assistant-chat-mobile-notice__icon'
              />
              <span>This feature is best experienced on larger screens.</span>
            </div>
          )}

          <form className='assistant-chat-container__input-container' onSubmit={handleSubmit}>
            <div
              id="assistant-chat-input"
              data-name="assistant-chat-input"
              className='assistant-chat-container__input'
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-multiline
              ref={inputRef}
              onInput={handleEditableInput}
              onKeyDown={handleComposerKeyDown}
              onKeyUp={handleComposerKeyUp}
              onMouseUp={updateMentionAutocomplete}
              onFocus={handleEditableInput}
              onBlur={closeMentionAutocomplete}
              style={{
                color: chat.coordinator.stagedUserMessageId ? 'transparent' : undefined,
              }}
            />
            {isInputEmpty && !chat.coordinator.stagedUserMessageId && (
              <div className='assistant-chat-container__input-placeholder' aria-hidden>
                Send a message...
              </div>
            )}
            {mentionAutocomplete && (
              <div className='assistant-chat-container__mention-menu' role="listbox" aria-label="Model input mentions">
                {mentionOptions.length === 0 ? (
                  <div className='assistant-chat-container__mention-menu-empty'>No matching inputs</div>
                ) : (
                  mentionOptions.map((option, index) => (
                    <button
                      key={option.path}
                      type="button"
                      role="option"
                      aria-selected={mentionActiveIndex === index}
                      className={`assistant-chat-container__mention-menu-option${mentionActiveIndex === index ? ' is-active' : ''}`}
                      onMouseEnter={() => setMentionActiveIndex(index)}
                      onMouseDown={event => {
                        event.preventDefault();
                        insertMentionFromOption(option);
                      }}
                    >
                      <span className='assistant-chat-container__mention-menu-option-label'>{option.label}</span>
                    </button>
                  ))
                )}
              </div>
            )}
            <Button
              type="submit"
              className='assistant-chat-container__input-container__button'
              minimal
              icon='send-message'
              disabled={
                chat.status === AssistantChatStatus.SUBMITTED
                || chat.status === AssistantChatStatus.STREAMING
                || hasPendingApprovals
              }
              aria-label='Send message'
            />

            {chat.coordinator.stagedUserMessageId && (
              <motion.div
                layoutId={`message-container-${chat.coordinator.stagedUserMessageId}`}
                className='assistant-chat-container__input-container__animation'
                initial={{
                  width: '100%',
                  opacity: 0.3,
                }}
                animate={{
                  width: 'fit-content',
                  translateY: '-110%',
                  translateX: '-10%',
                  scale: 0.7,
                  opacity: 0.5,
                }}
                transition={{
                  duration: 0.3,
                  ease: 'easeIn'
                }}
                onAnimationComplete={chat.coordinator.handleUserMessageDidFinishStagingAnimation}
              >
                <motion.p
                  className='assistant-chat-container__input-container__animation-text'
                >
                  {chat.coordinator.stagedUserMessageContent}
                </motion.p>
              </motion.div>
            )}
          </form>
        </div>
      </div>
    </LayoutGroup>
  );
}
